import React from 'react';
import { useTranslation } from '../localization/useAppTranslation';
import { MessageAvatar } from './MessageAvatar';
import { MessageContent } from './MessageContent';

export interface CustomMessageProps {
  id: string | number;
  direction: 'incoming' | 'outgoing';
  sender: string;
  content: string;
  timestamp?: string | number;
  avatarSrc?: string;
  avatarAlt?: string;
  className?: string;
  children?: React.ReactNode;
  showAvatar?: boolean;
  isLoading?: boolean;
}

/**
 * Renders a message with avatar, content, and optional feedback actions.
 */
export const CustomMessage = React.forwardRef<HTMLDivElement, CustomMessageProps>(
  (
    {
      id,
      direction,
      sender,
      content,
      timestamp,
      avatarSrc,
      avatarAlt,
      className = '',
      children,
      showAvatar = true,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('chat');
    const messageClass = `custom-message custom-message--${direction}`;
    const containerClass = [messageClass, className].filter(Boolean).join(' ');
    const messageRole = direction === 'incoming'
      ? t('messages.assistant')
      : t('messages.user');

    return (
      <div
        ref={ref}
        className={containerClass}
        data-message-id={id}
        data-direction={direction}
        role="article"
        aria-label={t('messages.ariaLabel', { role: messageRole })}
      >
        {direction === 'incoming' && showAvatar && avatarSrc && (
          <MessageAvatar src={avatarSrc} alt={avatarAlt || messageRole} />
        )}

        <div className="custom-message__content-wrapper">
          {children || <MessageContent content={content} isLoading={isLoading} />}
        </div>

        {direction === 'outgoing' && showAvatar && avatarSrc && (
          <MessageAvatar src={avatarSrc} alt={avatarAlt || sender} />
        )}
      </div>
    );
  }
);

CustomMessage.displayName = 'CustomMessage';
